/**
 * Interactive vinyl turntable music player section.
 * Displays a 3-column layout: album sleeve (left), AT-LP120-style turntable
 * with draggable tonearm (center), and track list with progress scrubbing (right).
 * Selecting an album triggers a vinyl-travel animation from sleeve → platter.
 */
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useCallback, useEffect, useState } from "react";
import { useVinylPlayer, type VinylAlbum } from "@/hooks/useVinylPlayer";
import { useIsMobile } from "@/hooks/use-mobile";
import { type ScratchEffect } from "./vinyl/ScratchEffects";
import Turntable from "./vinyl/Turntable";
import VinylRecord from "./vinyl/VinylRecord";
import Tonearm from "./vinyl/Tonearm";
import AlbumStack from "./vinyl/AlbumStack";
import AlbumCarousel from "./vinyl/AlbumCarousel";
import TrackSelector from "./vinyl/TrackSelector";
import TiltCard from "./vinyl/TiltCard";
import VariableHeadline from "./VariableHeadline";
import BlurImage from "./BlurImage";
import aPlaceCalledNowCover from "@/assets/cover-a-place-called-now.jpg?w=400&format=webp";
import deeperCover from "@/assets/cover-deeper.jpg?w=400&format=webp";
import coverSongsCover from "@/assets/cover-cover-songs.jpg?w=400&format=webp";

import { audioUrl } from "@/lib/storage";

// Scratch sound files
const scratchSounds = [
  "/audio/scratch-1.aac",
  "/audio/scratch-2.aac",
  "/audio/scratch-3.aac",
];

// Album data — audio served from Cloud Storage bucket
const vinylAlbums: VinylAlbum[] = [
  {
    id: "deeper",
    title: "DEEPER",
    cover: deeperCover,
    tracks: [
      { title: "Deeper", src: audioUrl("Thursday_Jam_Deeper_20260212.mp3"), duration: 799, isOriginal: true },
      { title: "A Place Called Now", src: audioUrl("Thursday_Jam_A_Place_Called_Now_20250925.mp3"), duration: 653, isOriginal: true },
      { title: "Deeper / The First Time I Died", src: audioUrl("Thursday_Jam_Deeper_The_First_Time_I_Died_20250925.mp3"), duration: 958, isOriginal: true },
      { title: "If U Can't Stand", src: audioUrl("Thursday_Jam_If_U_Cant_Stand_20260212.mp3"), duration: 587, isOriginal: true },
      { title: "Reaching For The Stars (Rocking Version)", src: audioUrl("Thursday_Jam_Reaching_For_The_Stars_Rocking_Version_20260129.mp3"), duration: 698, isOriginal: true },
      { title: "Thursday Jazz", src: audioUrl("Thursday_Jam_Thursday_Jazz_20230317.mp3"), duration: 713, isOriginal: true },
      { title: "Time For Me Is Nothing", src: audioUrl("Thursday_Jam_Time_For_Me_Is_Nothing_20250925.mp3"), duration: 1310, isOriginal: true },
      { title: "Underneath The Pale Moonlight", src: audioUrl("Thursday_Jam_Underneath_The_Pale_Moonlight_20230504.mp3"), duration: 1110, isOriginal: true },
      { title: "Welcome To The Thursday Jam", src: audioUrl("Thursday_Jam_Welcome_To_The_Thursday_Jam_20251002.mp3"), duration: 736, isOriginal: true },
    ],
  },
  {
    id: "a-place-called-now",
    title: "A PLACE CALLED NOW",
    cover: aPlaceCalledNowCover,
    tracks: [
      { title: "A Place Called Now", src: audioUrl("Thursday_Jam_A_Place_Called_Now_20260122.mp3"), duration: 401, isOriginal: true },
      { title: "Come A Little Closer", src: audioUrl("Thursday_Jam_Come_A_Little_Closer_20251009.mp3"), duration: 226, isOriginal: true },
      { title: "Deeper", src: audioUrl("Thursday_Jam_Deeper_20251014.mp3"), duration: 445, isOriginal: true },
      { title: "I Was Supposed To Die Today", src: audioUrl("Thursday_Jam_I_Was_Supposed_To_Die_Today_20260129.mp3"), duration: 494, isOriginal: true },
      { title: "Kriegn Wa Alllet Hin (Leni Bass)", src: audioUrl("Thursday_Jam_Kriegn_Wa_Alllet_Hin_Leni_Bass_20250415.mp3"), duration: 334, isOriginal: true },
      { title: "Reaching For The Stars (Acustic Version)", src: audioUrl("Thursday_Jam_Reaching_For_The_Stars_Acustic_Version_20251127.mp3"), duration: 316, isOriginal: true },
      { title: "Reaching For The Stars (Mareike Birthday Version)", src: audioUrl("Thursday_Jam_Reaching_For_The_Stars_Birthday_Version_20251113.mp3"), duration: 585, isOriginal: true },
      { title: "Searching For The Truth", src: audioUrl("Thursday_Jam_Searching_For_The_Truth_20260129.mp3"), duration: 335, isOriginal: true },
      { title: "Welcome To The Thursday Jam", src: audioUrl("Thursday_Jam_Welcome_To_The_Thursday_Jam_20251014.mp3"), duration: 434, isOriginal: true },
    ],
  },
  {
    id: "cover-songs",
    title: "COVER SONGS",
    cover: coverSongsCover,
    tracks: [
      { title: "A Fool For Your Stockings", src: audioUrl("Thursday_Jam_A_Fool_For_Your_Stockings_20251106.mp3"), duration: 248, artist: "ZZ Top", isOriginal: false },
      { title: "All Along The Watchtower", src: audioUrl("Thursday_Jam_All_Along_The_Watchtower_20251002.mp3"), duration: 386, artist: "Jimi Hendrix", isOriginal: false },
      { title: "Cortez The Killer", src: audioUrl("Thursday_Jam_Cortez_The_Killer_20250508.mp3"), duration: 694, artist: "Neil Young", isOriginal: false },
      { title: "Fly Like An Eagle", src: audioUrl("Thursday_Jam_Fly_Like_An_Eagle_20260219.mp3"), duration: 198, artist: "Steve Miller Band", isOriginal: false },
      { title: "Hey Hey My My", src: audioUrl("Thursday_Jam_Hey_Hey_My_My_20260129.mp3"), duration: 353, artist: "Neil Young", isOriginal: false },
      { title: "I Loved Another Woman", src: audioUrl("Thursday_Jam_I_Loved_Another_Woman_20260129.mp3"), duration: 161, artist: "Fleetwood Mac", isOriginal: false },
      { title: "Mary Had A Little Lamb", src: audioUrl("Thursday_Jam_Mary_Had_A_Little_Lamb_20251014.mp3"), duration: 184, artist: "Stevie Ray Vaughan", isOriginal: false },
      { title: "Rocking In The Free World", src: audioUrl("Thursday_Jam_Rocking_In_The_Free_World_20251014.mp3"), duration: 302, artist: "Neil Young", isOriginal: false },
      { title: "The Bluest Blues", src: audioUrl("Thursday_Jam_The_Bluest_Blues_20260219.mp3"), duration: 373, artist: "Alvin Lee", isOriginal: false },
      { title: "White Room", src: audioUrl("Thursday_Jam_White_Room_20260226.mp3"), duration: 362, artist: "Cream", isOriginal: false },
      { title: "You Really Got Me", src: audioUrl("Thursday_Jam_You_Really_Got_Me_20230425.mp3"), duration: 140, artist: "The Kinks", isOriginal: false },
      { title: "Komplett im Arsch", src: audioUrl("Thursday_Jam_Ich_Bin_Komplett_Im_Arsch_20250415.mp3"), duration: 433, artist: "Feine Sahne Fischfilet", isOriginal: false },
    ],
  },
];

const VinylPlayerSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const isMobile = useIsMobile();

  const {
    state,
    currentAlbum,
    currentTrackIndex,
    progress,
    isSpinning,
    vinylPosition,
    tonearmAngle,
    tonearmLifted,
    volume,
    isBusy,
    selectAlbum,
    togglePlayPause,
    seekTrack,
    seekInTrack,
    seekToAngle,
    setTonearmLiftedDirectly,
    stop,
    scratch,
    setHoveredAlbum,
    setVolume,
  } = useVinylPlayer();

  // Preload scratch sound files on mount for zero-latency playback
  const scratchAudiosRef = useRef<HTMLAudioElement[]>([]);
  useEffect(() => {
    scratchAudiosRef.current = scratchSounds.map(src => {
      const audio = new Audio(src);
      audio.volume = 0.6;
      audio.load();
      return audio;
    });
  }, []);

  // Scratch effects state (rendered in non-rotating Turntable overlay)
  const [scratchEffects, setScratchEffects] = useState<ScratchEffect[]>([]);

  const isPlaying = state === 'playing';
  // Tonearm is "active" when vinyl is on the platter and we're playing or paused
  const isTonearmActive = currentAlbum !== null && 
    (state === 'playing' || state === 'paused' || state === 'seeking' || state === 'cueing');
  const canControl = currentAlbum !== null && 
    state !== 'selecting' && state !== 'loading' && state !== 'unloading' && state !== 'idle';

  // Handle vinyl click: scratch if playing, unload if paused on platter
  const handleVinylClick = useCallback((x: number, y: number) => {
    if (vinylPosition !== 'platter') return;

    if (isPlaying) {
      // Add scratch ripple effect
      const newEffect = { id: Date.now(), x, y };
      setScratchEffects(prev => [...prev, newEffect]);
      setTimeout(() => {
        setScratchEffects(prev => prev.filter(e => e.id !== newEffect.id));
      }, 600);

      // Pause music, play scratch, rewind & resume
      const resume = scratch();
      if (resume) {
        const randomIndex = Math.floor(Math.random() * scratchAudiosRef.current.length);
        const scratchAudio = scratchAudiosRef.current[randomIndex];
        // Clone to allow rapid re-clicks
        const clone = scratchAudio.cloneNode() as HTMLAudioElement;
        clone.volume = 0.6;
        clone.onended = () => resume();
        clone.play().catch(() => resume());
      }
  } else if (state === 'paused' && !isSpinning && !isBusy) {
      // Click resting vinyl → slide back into sleeve (only when fully at rest)
      stop(true);
    }
  }, [isPlaying, state, vinylPosition, isSpinning, isBusy, scratch, stop]);

  // Handle turntable start/stop - select first album if none selected
  const handleTurntableToggle = useCallback(() => {
    if (!currentAlbum && state === 'idle') {
      // No album selected - load the first album
      selectAlbum(vinylAlbums[0]);
    } else {
      togglePlayPause();
    }
  }, [currentAlbum, state, selectAlbum, togglePlayPause]);

  // Handle tonearm drag
  const handleTonearmDragStart = useCallback(() => {
    // Lift the tonearm when starting to drag
    setTonearmLiftedDirectly(true);
  }, [setTonearmLiftedDirectly]);

  const handleTonearmDragEnd = useCallback(async (displayAngle: number) => {
    if (!currentAlbum) {
      // No album loaded - just return to rest
      return;
    }
    
    // Convert display angle to hook angle (they're negated)
    const hookAngle = -displayAngle;
    
    // Seek to the dropped position
    await seekToAngle(hookAngle);
  }, [currentAlbum, seekToAngle]);

  // Shared height - 10% smaller to give track list more room
  const playerHeightClass = "h-[min(50vh,340px)]";

  const featuredAlbum = currentAlbum ?? vinylAlbums[0];

  return (
    <section
      id="vinyl-player"
      ref={sectionRef}
      className="section-container bg-background overflow-hidden scroll-mt-6"
    >
      <div className="mx-auto max-w-6xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mb-16 lg:mb-20"
        >
          <motion.div
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 1.2, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="mb-6 h-px w-24 origin-left bg-foreground/30"
          />
          <VariableHeadline 
            as="h2" 
            pattern="wave"
            className="text-5xl sm:text-6xl md:text-8xl"
          >
            Vinyl Experience
          </VariableHeadline>
          <p className="mt-4 font-body text-base text-muted-foreground max-w-xl">
            Select a record from the stack and watch it come to life on the turntable.
          </p>
        </motion.div>

        {/* Mobile: Cover flow carousel above turntable */}
        <div className="lg:hidden mb-6">
          <AlbumCarousel
            albums={vinylAlbums}
            currentAlbumId={currentAlbum?.id ?? null}
            vinylPosition={vinylPosition}
            onSelectAlbum={selectAlbum}
          />
        </div>

        {/* Main Layout - Featured Sleeve + Turntable + Tracklist */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-1 lg:grid-cols-[auto_auto_minmax(220px,280px)] gap-6 lg:gap-8 items-start justify-start"
          style={{ perspective: "1200px" }}
        >
          {/* Left: Featured sleeve (full height) - desktop only */}
          <div className="hidden lg:block w-full relative z-20">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Record Sleeve
            </p>
            {/* Featured sleeve: SAME HEIGHT as turntable */}
            <div className={"w-full flex items-start " + playerHeightClass}>
              <AnimatePresence mode="popLayout">
                <motion.div
                  key={featuredAlbum.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="h-full"
                >
                  <TiltCard
                    onClick={() => selectAlbum(featuredAlbum)}
                    className="h-full aspect-square"
                    ariaLabel={`Select ${featuredAlbum.title}`}
                    vinylPeekOnHover={true}
                    isVinylOut={vinylPosition !== 'sleeve'}
                  >
                    <BlurImage
                      src={featuredAlbum.cover}
                      alt={featuredAlbum.title}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </TiltCard>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Center: Turntable with Vinyl and Tonearm - AT-LP120 style */}
          <div className="w-full flex flex-col">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Currently Playing{currentAlbum ? ` – ${currentAlbum.title}` : ""}
            </p>
            {/* Rectangular turntable (5:4) - overflow visible for vinyl travel */}
            <div className={"relative rounded-sm " + playerHeightClass}>
              <div className="relative h-full aspect-[5/4] overflow-visible">
              <Turntable isSpinning={isSpinning} isBusy={isBusy} isActive={state === 'playing' || state === 'cueing'} onTogglePlay={handleTurntableToggle} vinylPosition={vinylPosition} scratchEffects={scratchEffects}>
                <VinylRecord
                  albumCover={currentAlbum?.cover}
                  position={vinylPosition}
                  isVisible={currentAlbum !== null}
                  onVinylClick={handleVinylClick}
                />
              </Turntable>
              {/* Tonearm is now integrated into the turntable - DRAGGABLE */}
              <Tonearm 
                angle={tonearmAngle} 
                isLifted={tonearmLifted} 
                isActive={isTonearmActive}
                volume={volume}
                tracks={currentAlbum?.tracks}
                onVolumeChange={setVolume}
                onDragStart={handleTonearmDragStart}
                onDragEnd={handleTonearmDragEnd}
                onClick={handleTurntableToggle}
              />

              </div>
            </div>
          </div>

          {/* Right: Track List - same height as turntable including label */}
          <div className="w-full flex flex-col">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
              Track List
            </p>
            {currentAlbum ? (
              <div className={playerHeightClass + " overflow-y-auto scrollbar-thin scrollbar-thumb-muted scrollbar-track-transparent"}>
                <TrackSelector
                  tracks={currentAlbum.tracks}
                  currentTrackIndex={currentTrackIndex}
                  trackProgress={progress}
                  onSelectTrack={seekTrack}
                  onTogglePlayPause={togglePlayPause}
                  onSeekInTrack={seekInTrack}
                  isPlaying={isPlaying}
                />
              </div>
            ) : (
              <div className={playerHeightClass + " flex flex-col"}>
                <motion.div
                  className="flex-1 flex flex-col items-center justify-center text-center border border-dashed border-border rounded-lg"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                >
                  <p className="font-body text-sm text-muted-foreground">
                    Select a record to view tracks
                  </p>
                </motion.div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Other Records - Desktop only grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 hidden lg:block"
        >
          <p className="font-body text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">
            Other records
          </p>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-3">
            {vinylAlbums.map((album) => {
              const isThisVinylOnTurntable = currentAlbum?.id === album.id && vinylPosition !== 'sleeve';
              return (
                <TiltCard
                  key={album.id}
                  onClick={() => selectAlbum(album)}
                  className="aspect-square w-full"
                  ariaLabel={`Select ${album.title}`}
                  maxTilt={12}
                  vinylPeekOnHover={true}
                  isVinylOut={isThisVinylOnTurntable}
                >
                  <BlurImage
                    src={album.cover}
                    alt={album.title}
                    className="h-full w-full object-cover rounded-sm"
                    loading="lazy"
                  />
                  {currentAlbum?.id === album.id && (
                    <div className="absolute inset-0 ring-2 ring-foreground rounded-sm" />
                  )}
                </TiltCard>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VinylPlayerSection;
